import {
  $ngStateService,
  accountsService,
  csvExportService,
  columnsFetchService,
  numberService,
  numberPoolService,
  campaignService,
  offersService,
  affiliateService,
  utilities,
} from '@/angularBridgeCode/ringbaPortal.servicesModule';
import Store from '@/store';

export class NumbersViewModel {

  hasNumberPermissionAccess = false;
  canDeleteNumberPermission = false;
  isAdmin = false;
  isAffiliate = false;
  isLoading = true;

  /** selected by route, set only before init */
  selectedNumberId = null;

  /** State of number context menu
   *
   * @type { null | { mode: 'create' } | { mode: 'edit', id: string, number: ringba.data.numbers.CurrentNumber } }
   */
  activeNumber = null;

  /** @type {ringba.data.numbers.CurrentNumber[]} */
  numbers = [];
  campaigns = {};

  columns = [];
  columnSettings = {};

  selectedOptionDropdown = DefaultFilterOption;

  /** @type {{ type: 'confirm-pause' | 'confirm-delete' | 'select-affiliate' | 'confirm-remove-affiliate' | 'select-pool', number: any, onSubmit?: () => void | Promise<void> }} */
  modal = null;

  numberReferences = null;
  /** @type {'loading' | 'error' | 'ok' } */
  numberReferencesState = null;

  get showSpecificRecordDetails() {
    return !!this.selectedNumberId;
  }

  get currentAccountIsPaused() {
    return accountsService.currentAccountIsPaused;
  }

  /** number, for selected number when fetched */
  get selectedNumber() {
    return this.selectedNumberId
      ? this.numbers.find(n => n.id === this.selectedNumberId)
      : null;
  }

  get isCreateMode() {
    return this.activeNumber && this.activeNumber.mode === 'create';
  }

  get isEditMode() {
    return this.activeNumber && this.activeNumber.mode === 'edit';
  }

  getIsEditingNumberId(numberId) {
    return this.isEditMode && this.activeNumber.id === numberId;
  }

  getFilterOptions() {
    return FilterOptions;
  }

  getFilteredNumbers() {
    if (!this.numbers || !this.numbers.length) {
      return [];
    }

    const filter = FilterOptionsMap[this.selectedOptionDropdown];
    if (!filter) {
      return this.numbers;
    }

    return this.numbers.filter(filter);
  }

  getDisplayNumbers() {
    // for mobile view, only show the active number if selected
    if (this.isMobileView && this.activeNumber?.number) {
      return [this.activeNumber.number];
    }

    return this.getFilteredNumbers();
  }

  getActiveColumns() {
    return this.columns.filter(col => !this.columnSettings[col].hide);
  }

  canEditNumber(number) {
    return this.hasNumberPermissionAccess || (this.isAffiliate && number.offerId);
  }

  /** Injected translation helper */
  $t = (key) => {
    console.warn('$t (i18n) not initialized, key = ', key);
    return key;
  };

  withI18N($t) {
    if ($t) {
      this.$t = $t;
    }
    return this;
  }

  withSelectedNumberId(selectedNumberId) {
    this.selectedNumberId = selectedNumberId;
    return this;
  }

  async initialize() {

    this.columnSettings = numberService.columnSettings;

    this.hasNumberPermissionAccess = accountsService.hasAccess('Number', 'CREATE');
    this.canDeleteNumberPermission = accountsService.hasAccess('Number', 'DELETE');
    
    // Initialize in parallel
    this._loadAffiliates();
    
    this.isAdmin = await accountsService.isAdmin();
    this.isAffiliate = await accountsService.isAffiliate();
    this.isAffiliateManager = await accountsService.isAffiliateManager();

    if (!this.isAffiliate) {
      this._loadNumberPools();
    }

    try {
      await this._loadUserSettings();
      await this._loadCampaigns();
      await this._loadNumberData();
      await this._loadAllOffers();
    } catch (err) {
      this._errorHandler(err);
    } finally {
      this.isLoading = false;
    }
  }

  showForm(number) {
    this.hideForm(false); // make sure previous form is hidden

    if (!number) { // create mode requested
      if (this.hasNumberPermissionAccess) {
        this.activeNumber = { mode: 'create' };
      }
      return;
    }

    if (this.canEditNumber(number)) {
      this.activeNumber = {
        mode: 'edit',
        id: number.id,
        number,
      };
    }
  }

  hideForm(redirect = true) {
    this.activeNumber = null;

    if (this.showSpecificRecordDetails && redirect) {
      $ngStateService.go('dashboard.numbers');
    }
  }

  cancelModal() {
    this.modal = null;
  }

  canDeleteNumber(number) {
    return this.canDeleteNumberPermission || (this.isAffiliate && number.offerId);
  }

  confirmDeleteModal(number) {
    if (this.activeNumber || !this.canDeleteNumber(number)) {
      return;
    }

    this._fetchNumberReferences(number.id);

    this.modal = {
      type: 'confirm-delete',
      number,
      onSubmit: async () => {
        try {
          await numberService.deleteNumber(accountsService.currentAccountId, number.id, true);
          this.numbers.splice(this.numbers.indexOf(number), 1);
          this._showSuccessMessage(this.$t('DELETE_NUMBER_SUCCESSFUL_MESSAGE'));
        } catch (err) {
          this._errorHandler(err);
        }
        this.cancelModal();
      },
    };
  }

  confirmNumberPause(number) {
    if (this.activeNumber) {
      return;
    }

    // opens modal to confirm pausing number
    this.modal = {
      type: 'confirm-pause',
      number,
    };
  }

  onFilterChange(e) {
    this.selectedOptionDropdown = e.value;
  }

  onSave() {
    this._loadNumberData();

    // Don't hide form when specific record details are only being shown
    if (!this.showSpecificRecordDetails) {
      this.hideForm();
    }
  }

  exportNumbersCSV() {
    const numbersToExport = this.numbers.filter(number => {
      return FilterOptionsMap[this.selectedOptionDropdown](number);
    });

    csvExportService.exportCSVFile(
      csvExportService.exportNumbersHeaders,
      numbersToExport,
      'numbers',
    );
  }

  selectNumberPoolModal(number) {
    this.modal = {
      type: 'select-pool',
      number,
    };
  }

  async attachNumberPool(numberPoolId, numberId) {
    try {
      await numberPoolService.addNumber(accountsService.currentAccountId, numberPoolId, numberId);

      this._showSuccessMessage(this.$t('NUMBER_SELECT_NUMBER_POOL_ADD_SUCCESSFUL'));
      await this._loadNumberData();

      this.cancelModal();
    } catch (err) {
      this._errorHandler(err);
    }
  }

  async setNumberStatus(number, status) {
    number = number || this.modal.number;

    try {
      await numberService.updateNumber(
        accountsService.currentAccountId,
        number.id,
        { enabled: status },
      );
      this.cancelModal();

      number.enabled = status;

      this._showSuccessMessage(
        this.$t(status ? 'NUMBER_ENABLE_SUCCESS' : 'NUMBER_PAUSE_SUCCESS'),
      );
    } catch (err) {
      this._errorHandler(err);
    }
  }

  selectAffiliateModal(number) {
    this.modal = {
      type: 'select-affiliate',
      number,
    };
  }

  async attachAffiliate(affiliateId, numberId) {
    if (!numberId || !affiliateId) {
      this.cancelModal();
      return;
    }

    try {
      await numberService.attachAffiliate(accountsService.currentAccountId, numberId, affiliateId);
      await this._loadNumberData();
      this.cancelModal();
    } catch (err) {
      this._errorHandler(err);
    }
  }

  async removeAffiliate(number) {
    this.modal = {
      type: 'confirm-remove-affiliate',
      number,
      onSubmit: async () => {
        try {
          await numberService.removeAffiliate(accountsService.currentAccountId, number.id, number.publisherId);
          number.publisherId = null;
          await this._loadNumberData();
          this._showSuccessMessage(this.$t('NUMBER_SELECT_AFFILIATE_REMOVE_PUBLISHER_SUCCESSFUL'));
        } catch (err) {
          this._errorHandler(err);
        }

        this.cancelModal();
      },
    };
  }

  syncColumns() {
    this.columns = columnsFetchService.getColumnList(
      Store.state.ui.userSettings, // this.userSettings,
      numberService.defaultColumns,
      'numbers',
      this.columnSettings,
    );
  }

  async _fetchNumberReferences(numberId) {
    this.numberReferences = null;
    this.numberReferencesState = 'loading';
    try {
      const response = await numberService.getNumberReferences(accountsService.currentAccountId, numberId);

      // We're using timeout here to show the user the checking number references text
      setTimeout(() => {
        this.numberReferencesState = 'ok';
        this.numberReferences = response.data;
      }, 800);
    } catch {
      this.numberReferencesState = 'error';
    }
  }

  async _loadUserSettings() {
    Store.dispatch('ui/getUserSettings');
    this.syncColumns();
  }
  async _loadCampaigns() {
    const response = await campaignService.getCampaignsBasic(accountsService.currentAccountId);
    this.campaigns = {};
    if (!Array.isArray(response.data.campaigns)) {
      return;
    }

    response.data.campaigns.forEach(camp => {
      this.campaigns[camp.id] = {
        name: camp.name,
        isComplete: camp.completed
      };
    });
  }
  _loadNumberData() {
    if (this.selectedNumberId) {
      return this._loadNumber(this.selectedNumberId);
    } else {
      return this._loadNumbers();
    }
  }
  _mapNumberCampaigns(numbers) {
    numbers.forEach((number) => {
      const campaign = number.campaignId && this.campaigns[number.campaignId];
      if (campaign) {
        number.campaignName = campaign.name;
        number.isCampaignComplete = campaign.isComplete;
      }
    });
  }
  _mapNumberOffers(offers) {
    offers.forEach(offer => {
      const number = this.numbers.find(n => n.campaignId === offer.campaignId);
      if (number) {
        number.offerName = offer.name;
      }
    });
  }
  async _loadNumbers() {
    const response = await numberService.getNumbersUI(accountsService.currentAccountId);
    this.numbers = response.data.numbers;
    this._mapNumberCampaigns(this.numbers);
  }

  async _loadNumber(numberId) {
    const response = await numberService.getNumber(accountsService.currentAccountId, numberId);

    /** @type {ringba.data.numbers.CurrentNumber} */
    const number = response.data.number;

    if (!number.countryCode && number.assignmentSettings) {
      number.countryCode = number.assignmentSettings.countryCode;
    }

    if (!number.publisherId && number.affiliate) {
      number.publisherId = number.affiliate.id;
      number.publisherName = number.affiliate.name;
    }

    this.numbers = [number];
    this._mapNumberCampaigns(this.numbers);
    this.showForm(number);
  }

  async _loadAllOffers() {
    if (this.isAffiliate) {
      const response = await offersService.getAllOffers(accountsService.currentAccountId, true);
      this._mapNumberOffers(response.data.offers);
    }
  }

  async _loadAffiliates() {
    try {
      const response = await affiliateService.getAffiliates(accountsService.currentAccountId);
      this.affiliates = response.data.affiliates;
    }
    catch (err) {
      this._errorHandler(err);
    }
  }

  async _loadNumberPools() {
    try {
      const response = await numberPoolService.getNumberPools(accountsService.currentAccountId);
      this.numberPools = response.data.numberPools;
    }
    catch (err) {
      this._errorHandler(err);
    }
  }

  _showSuccessMessage(message) {
    utilities.showToaster('success', message);
  }

  _errorHandler(error) {
    utilities.errorHandler(error);
    // bsLoadingOverlayService.stop(); // TODO find the reference to it
  }
}


const FilterOptionsMap = {
  NUMBERS_FILTER_ALL: function () {
    return true;
  },
  NUMBERS_FILTER_NO_CAMPAIGN: function (number) {
    return !number.campaignId && !number.numberPoolId;
  },
  NUMBERS_FILTER_UNASSIGNED: function (number) {
    return (!number.campaignId && !number.publisherId) && !number.numberPoolId;
  },
  NUMBERS_FILTER_NO_PUBLISHER: function (number) {
    return !number.publisherId && !number.numberPoolId;
  },
  NUMBERS_FILTER_NOT_IN_POOL: function (number) {
    return !number.numberPoolId;
  },
  NUMBERS_FILTER_IN_POOL: function (number) {
    return number.numberPoolId;
  }
};

/** @type {(keyof FilterOptionsMap)[]} */
const FilterOptions = Object.keys(FilterOptionsMap);
/** @type {keyof FilterOptionsMap} */
const DefaultFilterOption = 'NUMBERS_FILTER_NOT_IN_POOL';
